import { useState } from 'react';
import { Alert, Button, Modal, Textarea, Stack, Group, Text } from '@mantine/core';
import { IconLock, IconLockOpen, IconAlertTriangle } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * Foydalanuvchini bloklash/blokdan chiqarish — sabab bilan (barcha rollar uchun).
 * Bloklashda sabab MAJBURIY: saqlanadi va (ilova foydalanuvchisi bo'lsa) unga
 * bildirishnoma orqali ko'rsatiladi. `patchPath` — PATCH { isActive, blockReason }
 * qabul qiladigan endpoint (masalan `/staff/:id` yoki `/premium/parents/:id`).
 *
 * ESLATMA: `/premium/parents/:id` — TARIXIY yo'l nomi (ota-ona profili), premium
 * obunasiga aloqasi yo'q va premium muzlatilganda ham ishlaydi: backend faqat
 * tanasida premium maydoni (`premiumUntil`/`premiumMonths`/...) bo'lsa 403 beradi,
 * bu komponent esa faqat `isActive` + `blockReason` yuboradi.
 */
export function BlockControl({ patchPath, isActive, blockReason, onDone }: {
  patchPath: string;
  isActive: boolean;
  blockReason?: string | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: (v: { isActive: boolean; blockReason?: string }) => api.patch(patchPath, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      setOpen(false);
      setReason('');
      onDone();
    },
    onError: (e: unknown) =>
      notifications.show({ color: 'red', message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error') }),
  });

  if (!isActive) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('block.blocked')}>
        <Stack gap="sm">
          {blockReason && <Text size="sm">{t('block.reasonLabel')}: <b>{blockReason}</b></Text>}
          <Button size="xs" color="teal" leftSection={<IconLockOpen size={15} />} w="fit-content"
            loading={mut.isPending} onClick={() => mut.mutate({ isActive: true })}>
            {t('block.unblock')}
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <>
      <Button variant="light" color="red" leftSection={<IconLock size={16} />} onClick={() => setOpen(true)}>
        {t('block.block')}
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title={t('block.title')} centered>
        <Stack>
          <Text size="sm" c="dimmed">{t('block.hint')}</Text>
          <Textarea
            label={t('block.reasonLabel')}
            placeholder={t('block.reasonPlaceholder')}
            autosize minRows={2} maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button color="red" disabled={!reason.trim()} loading={mut.isPending}
              onClick={() => mut.mutate({ isActive: false, blockReason: reason.trim() })}>
              {t('block.block')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
